import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM } from '../../design/design-system';
import type { PrintOptions } from './index';

/**
 * 가장자리 알림 — 옵션 변경이 미리보기 뷰포트 밖 섹션에 반영됐을 때만 잠깐 뜨는 방향 알약.
 * 약사 인쇄 화면(index.tsx)과 케어링노트 탑재뷰(CaringNoteEmbedPage)가 같은 동작을 공유한다.
 * 자동 스크롤은 보던 위치를 뺏어 정신사나우므로, 이동은 클릭했을 때만(opt-in).
 * (지니님 확정, 2026-09-05)
 */

/** 옵션 변경이 지면 어느 섹션에 반영되는지 — 가장자리 알림의 목적지 판정에 사용 */
export const ANCHOR_BY_OPTION: Partial<Record<keyof PrintOptions, string>> = {
  showPatientInfo: 'patient',
  showPrescriptionTable: 'prescription',
  prescriptionViewMode: 'prescription',
  showDrugImages: 'prescription',
  markEndedDrugs: 'prescription',
  classificationDisplay: 'prescription',
  drugScope: 'prescription',
  drugScopeAddMentioned: 'prescription',
  drugScopeMentionedStyle: 'prescription',
  hidePharmacistReport: 'analysis',
  hidePatientGuide: 'analysis',
  patientGuideCardSize: 'analysis',
};

export const ChangePill = styled.button<{ $direction: 'up' | 'down' }>`
  position: absolute;
  ${(props) => (props.$direction === 'up' ? 'top: 16px;' : 'bottom: 16px;')}
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  border: none;
  cursor: pointer;
  background: ${COLOR_SYSTEM.get("Gray")?.[800]};
  color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  font-size: 12px;
  padding: 8px 14px;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[700]};
  }

  @media print {
    display: none;
  }
`;

export interface ChangeNoticeState {
  anchor: string;
  direction: 'up' | 'down';
}

/**
 * 가장자리 알림 훅.
 * - `containerRef`를 스크롤 컨테이너(뷰포트 판정 기준)에 단다
 * - 옵션 변경 핸들러에서 setState **직전에** `recordChange(option)`을 부른다
 * - 반환된 `changePill`이 truthy면 ChangePill을 렌더하고 onClick에 `handlePillClick`을 단다
 */
export function useChangeNotice(printOptions: PrintOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingAnchorRef = useRef<string | null>(null);
  const pillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [changePill, setChangePill] = useState<ChangeNoticeState | null>(null);

  const recordChange = useCallback((option: keyof PrintOptions) => {
    pendingAnchorRef.current = ANCHOR_BY_OPTION[option] ?? null;
  }, []);

  // 옵션 반영 렌더가 끝난 뒤, 영향 섹션이 미리보기 뷰포트 밖이면 방향 알림을 띄운다.
  // 보이는 범위에서 바뀌었으면 아무것도 하지 않는다(미리보기 자체가 피드백).
  useEffect(() => {
    const anchor = pendingAnchorRef.current;
    pendingAnchorRef.current = null;
    if (!anchor) return;

    const container = containerRef.current;
    const el = container?.querySelector<HTMLElement>(`[data-print-anchor="${anchor}"]`);
    if (!container || !el) return; // 섹션이 사라진 변경(예: 표시 해제)은 알림 대상 아님

    const c = container.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const direction = r.bottom < c.top ? 'up' : r.top > c.bottom ? 'down' : null;
    if (!direction) return;

    setChangePill({ anchor, direction });
    if (pillTimerRef.current) clearTimeout(pillTimerRef.current);
    pillTimerRef.current = setTimeout(() => setChangePill(null), 2500);
  }, [printOptions]);

  // 알림 클릭 → 변경 지점으로 스크롤 + 도착 위치를 잠깐 플래시로 표시
  const handlePillClick = useCallback(() => {
    if (!changePill) return;
    const el = containerRef.current?.querySelector<HTMLElement>(
      `[data-print-anchor="${changePill.anchor}"]`
    );
    setChangePill(null);
    if (pillTimerRef.current) clearTimeout(pillTimerRef.current);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.style.transition = 'background-color 0.4s';
    el.style.backgroundColor = 'rgba(255, 214, 0, 0.22)';
    setTimeout(() => {
      el.style.backgroundColor = '';
    }, 1400);
  }, [changePill]);

  return { containerRef, changePill, recordChange, handlePillClick };
}
